"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

const STRINGS: Record<
  Locale,
  {
    assets: Record<string, string>;
    expectedReturn: string;
    riskStdDev: string;
    risk: string;
    efficientFrontier: string;
    currentPortfolio: string;
    afterOptimization: string;
    lowerRisk: string;
    raiseReturn: string;
    efTitle: string;
    efDescription: string;
    smallChange: string;
    inputA: string;
    inputB: string;
    equityRet: (pct: string) => string;
    bondRet: (pct: string) => string;
    optimalA: string;
    optimalB: string;
    changedOnly: string;
    equitySwing: string;
    sensitivityNote: string;
    mvTitle: string;
    mvDescription: string;
    marketEquilibrium: string;
    marketEquilibriumSub: string;
    investorView: string;
    investorViewSub: string;
    blendSub: string;
    stableAllocation: string;
    stableAllocationSub: string;
    allocationComparison: string;
    meanVarianceRaw: string;
    mvSkews1: string;
    mvSkews2: string;
    blBase1: string;
    blBase2: string;
    blTitle: string;
    blDescription: string;
    personas: { label: string }[];
    axisHint: string;
    rtTitle: string;
    rtDescription: string;
  }
> = {
  ja: {
    assets: {
      domesticBond: "国内債券",
      developedEquity: "先進国株式",
      emergingEquity: "新興国株式",
      equity: "株式",
      bond: "債券",
      cash: "預金",
      gold: "金",
      reit: "REIT",
    },
    expectedReturn: "期待リターン",
    riskStdDev: "リスク（標準偏差）",
    risk: "リスク",
    efficientFrontier: "効率的フロンティア",
    currentPortfolio: "今のポートフォリオ",
    afterOptimization: "最適化後",
    lowerRisk: "リスクを下げつつ",
    raiseReturn: "リターンを上げる",
    efTitle: "効率的フロンティアと現在のポートフォリオ",
    efDescription:
      "同じリスクでより高いリターンを、または同じリターンでより低いリスクを狙う。最適化は配分をフロンティアに近づける作業です。",
    smallChange: "期待リターンをわずかに変えると…",
    inputA: "入力A",
    inputB: "入力B",
    equityRet: (pct) => `株式 ${pct}`,
    bondRet: (pct) => `債券 ${pct}`,
    optimalA: "最適配分A",
    optimalB: "最適配分B",
    changedOnly: "株式の期待リターンを 7.0% → 5.5% に変更しただけ",
    equitySwing: "株式が 75% → 20% へ大きく変動",
    sensitivityNote: "入力の小さな差が、出力を大きく変えてしまう",
    mvTitle: "平均分散最適化は入力に敏感",
    mvDescription:
      "期待リターンの見積もりをわずかに変えるだけで、最適配分が大きく変わることがあります。これが平均分散最適化の実務上の課題です。",
    marketEquilibrium: "市場均衡",
    marketEquilibriumSub: "市場が織り込む期待リターン",
    investorView: "投資家のビュー",
    investorViewSub: "「日本株はやや強気」など",
    blendSub: "市場＋ビューを信頼度で合成",
    stableAllocation: "安定した配分",
    stableAllocationSub: "極端になりにくい",
    allocationComparison: "配分の比較",
    meanVarianceRaw: "平均分散（そのまま）",
    mvSkews1: "平均分散は",
    mvSkews2: "株式に極端に寄る",
    blBase1: "BLは市場を土台に",
    blBase2: "少しだけ傾ける",
    blTitle: "Black-Litterman：市場を土台にビューを重ねる",
    blDescription:
      "市場均衡を基準に、自分の見方を信頼度つきで加えます。主観だけで始めないため、極端な配分になりにくくなります。",
    personas: [{ label: "値動きを抑えたい" }, { label: "中間を狙いたい" }, { label: "成長を狙いたい" }],
    axisHint: "← 安定寄り\u3000\u3000\u3000\u3000\u3000\u3000\u3000\u3000成長寄り →",
    rtTitle: "フロンティアのどこを選ぶかは、リスク許容度で決まる",
    rtDescription:
      "左側ほど値動きを抑えやすく、右側ほどリターンを狙いやすくなります。ドットから伸びる配分バーが、その位置での保有割合の例です。",
  },
  en: {
    assets: {
      domesticBond: "Domestic bonds",
      developedEquity: "Developed equities",
      emergingEquity: "EM equities",
      equity: "Equities",
      bond: "Bonds",
      cash: "Deposits",
      gold: "Gold",
      reit: "REIT",
    },
    expectedReturn: "Expected return",
    riskStdDev: "Risk (standard deviation)",
    risk: "Risk",
    efficientFrontier: "Efficient frontier",
    currentPortfolio: "Your current portfolio",
    afterOptimization: "After optimising",
    lowerRisk: "Lower the risk while",
    raiseReturn: "raising the return",
    efTitle: "The efficient frontier and your current portfolio",
    efDescription:
      "Aim for a higher return at the same risk, or lower risk at the same return. Optimising moves the allocation towards the frontier.",
    smallChange: "Change the expected return slightly and…",
    inputA: "Input A",
    inputB: "Input B",
    equityRet: (pct) => `Equities ${pct}`,
    bondRet: (pct) => `Bonds ${pct}`,
    optimalA: "Optimal mix A",
    optimalB: "Optimal mix B",
    changedOnly: "Only the equity expected return changed, from 7.0% to 5.5%",
    equitySwing: "Equities swing from 75% to 20%",
    sensitivityNote: "A small difference in the input changes the output a great deal",
    mvTitle: "Mean-variance optimisation is sensitive to its inputs",
    mvDescription:
      "A slight change to your expected-return estimate can shift the optimal mix substantially. This is the practical difficulty with mean-variance optimisation.",
    marketEquilibrium: "Market equilibrium",
    marketEquilibriumSub: "The expected return the market prices in",
    investorView: "The investor's view",
    investorViewSub: "e.g. “mildly bullish on Japanese equities”",
    blendSub: "Blends market and view by confidence",
    stableAllocation: "A stable allocation",
    stableAllocationSub: "Less likely to go to extremes",
    allocationComparison: "Comparing the allocations",
    meanVarianceRaw: "Mean-variance (as is)",
    mvSkews1: "Mean-variance skews",
    mvSkews2: "heavily into equities",
    blBase1: "BL starts from the market",
    blBase2: "and tilts only slightly",
    blTitle: "Black-Litterman: layering views on top of the market",
    blDescription:
      "Starting from market equilibrium, you add your own view with a confidence level. Because you do not start from opinion alone, extreme allocations are less likely.",
    personas: [
      { label: "Wants to limit movement" },
      { label: "Wants something in between" },
      { label: "Wants growth" },
    ],
    axisHint: "← More stable                More growth-oriented →",
    rtTitle: "Where on the frontier you sit is set by your risk tolerance",
    rtDescription:
      "Further left limits movement more; further right reaches for return. The allocation bars extending from each dot are examples of the holdings at that point.",
  },
};

// ════════════════════════════════════════════════════════════════════
// 1. Efficient Frontier — current portfolio vs frontier
// ════════════════════════════════════════════════════════════════════

const EF_VW = 480;
const EF_VH = 320;
const EF_PAD = { top: 28, right: 24, bottom: 48, left: 56 };
const EF_CW = EF_VW - EF_PAD.left - EF_PAD.right;
const EF_CH = EF_VH - EF_PAD.top - EF_PAD.bottom;

// Axis ranges
const EF_X_MAX = 25; // risk %
const EF_Y_MAX = 12; // return %

function efX(risk: number) {
  return EF_PAD.left + (risk / EF_X_MAX) * EF_CW;
}
function efY(ret: number) {
  return EF_PAD.top + ((EF_Y_MAX - ret) / EF_Y_MAX) * EF_CH;
}

// Frontier curve: generated from a smooth sqrt-like function
// return = a * sqrt(risk - minRisk) + minReturn
// This produces the classic concave shape without wobble
const FRONTIER = (() => {
  const minRisk = 3.5;
  const minRet = 1.5;
  const scale = 2.8;
  const pts: { r: number; ret: number }[] = [];
  for (let r = minRisk; r <= 24; r += 0.5) {
    pts.push({ r, ret: minRet + scale * Math.sqrt(r - minRisk) });
  }
  return pts;
})();

// Individual assets
const ASSETS = [
  { key: "domesticBond", r: 3, ret: 1.5, color: COLOR.amber },
  { key: "developedEquity", r: 16, ret: 8.0, color: COLOR.sky },
  { key: "emergingEquity", r: 22, ret: 7.5, color: "#7c3aed" },
  { key: "reit", r: 14, ret: 6.0, color: "#ec4899" },
];

// User's current portfolio (below frontier)
const CURRENT = { r: 14, ret: 5.5 };
// Optimized point on frontier — computed from the same formula
const OPTIMIZED = (() => {
  const r = 10;
  const minRisk = 3.5;
  const ret = 1.5 + 2.8 * Math.sqrt(r - minRisk);
  return { r, ret };
})();

function buildPolylinePath(pts: { x: number; y: number }[]) {
  return pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
}

function EfficientFrontierIllustration() {
  const t = STRINGS[useDocLocale()];
  const frontierPts = FRONTIER.map((f) => ({ x: efX(f.r), y: efY(f.ret) }));
  const curvePath = buildPolylinePath(frontierPts);

  const xTicks = [0, 5, 10, 15, 20, 25];
  const yTicks = [0, 2, 4, 6, 8, 10, 12];

  return (
    <svg viewBox={`0 0 ${EF_VW} ${EF_VH}`} className="mx-auto w-full max-w-2xl">
      {/* Grid */}
      {yTicks.map((v) => (
        <g key={`y${v}`}>
          <line
            x1={EF_PAD.left}
            y1={efY(v)}
            x2={EF_VW - EF_PAD.right}
            y2={efY(v)}
            stroke="currentColor"
            strokeWidth="0.5"
            strokeOpacity={0.08}
          />
          <text x={EF_PAD.left - 8} y={efY(v) + 3} textAnchor="end" fontSize="8" fill="currentColor">
            {v}%
          </text>
        </g>
      ))}
      {xTicks.map((v) => (
        <g key={`x${v}`}>
          <line
            x1={efX(v)}
            y1={EF_PAD.top}
            x2={efX(v)}
            y2={EF_VH - EF_PAD.bottom}
            stroke="currentColor"
            strokeWidth="0.5"
            strokeOpacity={0.08}
          />
          <text x={efX(v)} y={EF_VH - EF_PAD.bottom + 14} textAnchor="middle" fontSize="8" fill="currentColor">
            {v}%
          </text>
        </g>
      ))}

      {/* Axes */}
      <line
        x1={EF_PAD.left}
        y1={EF_PAD.top}
        x2={EF_PAD.left}
        y2={EF_VH - EF_PAD.bottom}
        stroke="currentColor"
        strokeWidth="1"
      />
      <line
        x1={EF_PAD.left}
        y1={EF_VH - EF_PAD.bottom}
        x2={EF_VW - EF_PAD.right}
        y2={EF_VH - EF_PAD.bottom}
        stroke="currentColor"
        strokeWidth="1"
      />
      <text x={EF_PAD.left} y={EF_PAD.top - 10} fontSize="9" fontWeight="700" fill="currentColor">
        {t.expectedReturn}
      </text>
      <text x={EF_VW - EF_PAD.right} y={EF_VH - 10} textAnchor="end" fontSize="9" fontWeight="700" fill="currentColor">
        {t.riskStdDev}
      </text>

      {/* Frontier area fill */}
      <path
        d={`${curvePath} L ${frontierPts[frontierPts.length - 1].x} ${efY(0)} L ${frontierPts[0].x} ${efY(0)} Z`}
        fill={COLOR.sky}
        fillOpacity={0.04}
      />

      {/* Frontier curve */}
      <path d={curvePath} fill="none" stroke={COLOR.sky} strokeWidth="2.5" strokeLinecap="round" />
      <text x={efX(21)} y={efY(10.2)} fontSize="8.5" fill={COLOR.sky} fontWeight="700">
        {t.efficientFrontier}
      </text>

      {/* Individual assets */}
      {ASSETS.map((a) => (
        <g key={a.key}>
          <circle
            cx={efX(a.r)}
            cy={efY(a.ret)}
            r={4}
            fill={a.color}
            fillOpacity={0.2}
            stroke={a.color}
            strokeWidth="1.2"
          />
          <text x={efX(a.r) + 7} y={efY(a.ret) + 3} fontSize="7.5" fill={a.color} fontWeight="600">
            {t.assets[a.key]}
          </text>
        </g>
      ))}

      {/* Current portfolio */}
      <circle
        cx={efX(CURRENT.r)}
        cy={efY(CURRENT.ret)}
        r={6}
        className="fill-white dark:fill-zinc-900"
        stroke={COLOR.rose}
        strokeWidth="2"
      />
      <circle cx={efX(CURRENT.r)} cy={efY(CURRENT.ret)} r={2.5} fill={COLOR.rose} />
      <text x={efX(CURRENT.r) + 10} y={efY(CURRENT.ret) + 3} fontSize="8.5" fill={COLOR.rose} fontWeight="700">
        {t.currentPortfolio}
      </text>

      {/* Optimized point */}
      <circle
        cx={efX(OPTIMIZED.r)}
        cy={efY(OPTIMIZED.ret)}
        r={6}
        className="fill-white dark:fill-zinc-900"
        stroke={COLOR.emerald}
        strokeWidth="2"
      />
      <circle cx={efX(OPTIMIZED.r)} cy={efY(OPTIMIZED.ret)} r={2.5} fill={COLOR.emerald} />
      <text
        x={efX(OPTIMIZED.r) - 10}
        y={efY(OPTIMIZED.ret) - 10}
        textAnchor="end"
        fontSize="8.5"
        fill={COLOR.emerald}
        fontWeight="700"
      >
        {t.afterOptimization}
      </text>

      {/* Arrow: current → optimized */}
      {(() => {
        const x1 = efX(CURRENT.r);
        const y1 = efY(CURRENT.ret);
        const x2 = efX(OPTIMIZED.r);
        const y2 = efY(OPTIMIZED.ret);
        const dx = x2 - x1;
        const dy = y2 - y1;
        const len = Math.sqrt(dx * dx + dy * dy);
        const ux = dx / len;
        const uy = dy / len;
        const ax = x2 - ux * 10;
        const ay = y2 - uy * 10;
        return (
          <g>
            <line x1={x1} y1={y1} x2={ax} y2={ay} stroke={COLOR.emerald} strokeWidth="1.5" strokeDasharray="4 3" />
            <path
              d={`M ${x2} ${y2} L ${x2 - ux * 8 + uy * 4} ${y2 - uy * 8 - ux * 4} L ${x2 - ux * 8 - uy * 4} ${y2 - uy * 8 + ux * 4} Z`}
              fill={COLOR.emerald}
            />
          </g>
        );
      })()}

      {/* Annotation */}
      <text
        x={efX((CURRENT.r + OPTIMIZED.r) / 2) - 20}
        y={efY((CURRENT.ret + OPTIMIZED.ret) / 2) + 4}
        fontSize="7.5"
        fill={COLOR.emerald}
        fontWeight="600"
      >
        {t.lowerRisk}
      </text>
      <text
        x={efX((CURRENT.r + OPTIMIZED.r) / 2) - 20}
        y={efY((CURRENT.ret + OPTIMIZED.ret) / 2) + 14}
        fontSize="7.5"
        fill={COLOR.emerald}
        fontWeight="600"
      >
        {t.raiseReturn}
      </text>
    </svg>
  );
}

export function DocEfficientFrontierDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.efTitle}
      description={t.efDescription}
    >
      <EfficientFrontierIllustration />
    </IllustrationCard>
  );
}

// ════════════════════════════════════════════════════════════════════
// 2. MV Sensitivity — small input change → big allocation change
// ════════════════════════════════════════════════════════════════════

type AllocBar = { key: string; pct: number; color: string };

const MV_VW = 480;
const MV_VH = 260;

const CASE_A: AllocBar[] = [
  { key: "equity", pct: 75, color: COLOR.sky },
  { key: "bond", pct: 5, color: COLOR.amber },
  { key: "reit", pct: 15, color: "#ec4899" },
  { key: "cash", pct: 5, color: "#71717a" },
];

const CASE_B: AllocBar[] = [
  { key: "equity", pct: 20, color: COLOR.sky },
  { key: "bond", pct: 45, color: COLOR.amber },
  { key: "reit", pct: 30, color: "#ec4899" },
  { key: "cash", pct: 5, color: "#71717a" },
];

function HorizontalBar({
  x,
  y,
  w,
  h,
  bars,
  label,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  bars: AllocBar[];
  label: string;
}) {
  const t = STRINGS[useDocLocale()];
  // Pre-compute offsets to avoid reassignment during render
  const offsets = bars.reduce<number[]>((acc, _b, i) => {
    acc.push(i === 0 ? x : acc[i - 1] + (bars[i - 1].pct / 100) * w);
    return acc;
  }, []);

  return (
    <g>
      <text x={x} y={y - 8} fontSize="9" fill="currentColor" fontWeight="600">
        {label}
      </text>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={h / 2}
        fill="currentColor"
        fillOpacity={0.04}
        stroke="currentColor"
        strokeWidth="0.6"
        strokeOpacity={0.1}
      />
      {bars.map((b, i) => {
        const bw = (b.pct / 100) * w;
        const bx = offsets[i];
        return (
          <g key={t.assets[b.key]}>
            <rect x={bx} y={y} width={bw} height={h} rx={bx === x ? h / 2 : 0} fill={b.color} fillOpacity={0.2} />
            {bw > 30 && (
              <>
                <text
                  x={bx + bw / 2}
                  y={y + h / 2 - 1}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize="8"
                  fill={b.color}
                  fontWeight="600"
                >
                  {t.assets[b.key]}
                </text>
                <text
                  x={bx + bw / 2}
                  y={y + h / 2 + 10}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize="9"
                  fill={b.color}
                  fontWeight="700"
                >
                  {b.pct}%
                </text>
              </>
            )}
          </g>
        );
      })}
    </g>
  );
}

function MVSensitivityIllustration() {
  const t = STRINGS[useDocLocale()];
  const barW = 340;
  const barH = 36;
  const leftX = 120;

  return (
    <svg viewBox={`0 0 ${MV_VW} ${MV_VH}`} className="mx-auto w-full max-w-xl">
      {/* Input labels */}
      <text x={MV_VW / 2} y={20} textAnchor="middle" fontSize="10" fill="currentColor" fontWeight="700">
        {t.smallChange}
      </text>

      {/* Case A */}
      <text x={20} y={62} fontSize="8.5" fill={COLOR.sky} fontWeight="700">
        {t.inputA}
      </text>
      <text x={20} y={74} fontSize="7.5" fill={COLOR.sky}>
        {t.equityRet("7.0%")}
      </text>
      <text x={20} y={84} fontSize="7.5" fill={COLOR.sky}>
        {t.bondRet("2.0%")}
      </text>
      <HorizontalBar x={leftX} y={56} w={barW} h={barH} bars={CASE_A} label={t.optimalA} />

      {/* Change indicator */}
      <text x={MV_VW / 2} y={120} textAnchor="middle" fontSize="9" fill={COLOR.rose} fontWeight="700">
        {t.changedOnly}
      </text>
      <line
        x1={MV_VW / 2 - 80}
        y1={126}
        x2={MV_VW / 2 + 80}
        y2={126}
        stroke={COLOR.rose}
        strokeWidth="0.8"
        strokeDasharray="3 3"
      />

      {/* Case B */}
      <text x={20} y={156} fontSize="8.5" fill={COLOR.amber} fontWeight="700">
        {t.inputB}
      </text>
      <text x={20} y={168} fontSize="7.5" fill={COLOR.amber}>
        {t.equityRet("5.5%")}
      </text>
      <text x={20} y={178} fontSize="7.5" fill={COLOR.amber}>
        {t.bondRet("2.0%")}
      </text>
      <HorizontalBar x={leftX} y={150} w={barW} h={barH} bars={CASE_B} label={t.optimalB} />

      {/* Verdict */}
      <rect
        x={MV_VW / 2 - 140}
        y={208}
        width={280}
        height={36}
        rx={8}
        fill={COLOR.rose}
        fillOpacity={0.06}
        stroke={COLOR.rose}
        strokeWidth="0.8"
      />
      <text x={MV_VW / 2} y={224} textAnchor="middle" fontSize="9" fill={COLOR.rose} fontWeight="700">
        {t.equitySwing}
      </text>
      <text x={MV_VW / 2} y={237} textAnchor="middle" fontSize="8" fill={COLOR.rose}>
        {t.sensitivityNote}
      </text>
    </svg>
  );
}

export function DocMVSensitivityDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.mvTitle}
      description={t.mvDescription}
    >
      <MVSensitivityIllustration />
    </IllustrationCard>
  );
}

// ════════════════════════════════════════════════════════════════════
// 3. Black-Litterman — market equilibrium + views → stable output
// ════════════════════════════════════════════════════════════════════

const BL_VW = 520;
const BL_VH = 300;

function BLChip({
  x,
  y,
  w,
  h,
  label,
  sub,
  color,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  label: string;
  sub: string;
  color: string;
}) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={8} fill={color} fillOpacity={0.08} stroke={color} strokeWidth="1" />
      <text x={x + w / 2} y={y + h / 2 - 4} textAnchor="middle" fontSize="10" fill={color} fontWeight="700">
        {label}
      </text>
      <text x={x + w / 2} y={y + h / 2 + 10} textAnchor="middle" fontSize="8" fill={color}>
        {sub}
      </text>
    </g>
  );
}

function BLArrow({ x1, y1, x2, y2, color }: { x1: number; y1: number; x2: number; y2: number; color: string }) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  return (
    <g>
      <line x1={x1} y1={y1} x2={x2 - ux * 6} y2={y2 - uy * 6} stroke={color} strokeWidth="1.5" />
      <path
        d={`M ${x2} ${y2} L ${x2 - ux * 7 + uy * 3.5} ${y2 - uy * 7 - ux * 3.5} L ${x2 - ux * 7 - uy * 3.5} ${y2 - uy * 7 + ux * 3.5} Z`}
        fill={color}
      />
    </g>
  );
}

// Mini horizontal allocation bar
function MiniBar({ x, y, w, bars }: { x: number; y: number; w: number; bars: AllocBar[] }) {
  const t = STRINGS[useDocLocale()];
  const h = 18;
  const offsets = bars.reduce<number[]>((acc, _b, i) => {
    acc.push(i === 0 ? x : acc[i - 1] + (bars[i - 1].pct / 100) * w);
    return acc;
  }, []);
  return (
    <g>
      {bars.map((b, i) => {
        const bw = (b.pct / 100) * w;
        const bx = offsets[i];
        return (
          <g key={t.assets[b.key]}>
            <rect x={bx} y={y} width={bw} height={h} fill={b.color} fillOpacity={0.25} />
            {bw > 20 && (
              <text x={bx + bw / 2} y={y + h / 2 + 3} textAnchor="middle" fontSize="7" fill={b.color} fontWeight="700">
                {b.pct}%
              </text>
            )}
          </g>
        );
      })}
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={4}
        fill="none"
        stroke="currentColor"
        strokeWidth="0.6"
        strokeOpacity={0.15}
      />
    </g>
  );
}

function BlackLittermanIllustration() {
  const t = STRINGS[useDocLocale()];
  const boxW = 130;
  const boxH = 50;

  // Three inputs on the left
  const marketBox = { x: 20, y: 40 };
  const viewBox = { x: 20, y: 130 };

  // BL model in center
  const blBox = { x: 200, y: 80 };

  // Output on right
  const outBox = { x: 380, y: 80 };

  // Comparison bars at bottom
  const barY = 200;
  const barW = 200;

  const marketAlloc: AllocBar[] = [
    { key: "equity", pct: 55, color: COLOR.sky },
    { key: "bond", pct: 25, color: COLOR.amber },
    { key: "reit", pct: 12, color: "#ec4899" },
    { key: "cash", pct: 8, color: "#71717a" },
  ];

  const mvRawAlloc: AllocBar[] = [
    { key: "equity", pct: 80, color: COLOR.sky },
    { key: "bond", pct: 2, color: COLOR.amber },
    { key: "reit", pct: 15, color: "#ec4899" },
    { key: "cash", pct: 3, color: "#71717a" },
  ];

  const blAlloc: AllocBar[] = [
    { key: "equity", pct: 60, color: COLOR.sky },
    { key: "bond", pct: 20, color: COLOR.amber },
    { key: "reit", pct: 13, color: "#ec4899" },
    { key: "cash", pct: 7, color: "#71717a" },
  ];

  return (
    <svg viewBox={`0 0 ${BL_VW} ${BL_VH}`} className="mx-auto w-full max-w-2xl">
      {/* Input boxes */}
      <BLChip
        x={marketBox.x}
        y={marketBox.y}
        w={boxW}
        h={boxH}
        label={t.marketEquilibrium}
        sub={t.marketEquilibriumSub}
        color={COLOR.emerald}
      />
      <BLChip
        x={viewBox.x}
        y={viewBox.y}
        w={boxW}
        h={boxH}
        label={t.investorView}
        sub={t.investorViewSub}
        color={COLOR.amber}
      />

      {/* BL model box */}
      <BLChip
        x={blBox.x}
        y={blBox.y}
        w={boxW}
        h={boxH}
        label="Black-Litterman"
        sub={t.blendSub}
        color={COLOR.sky}
      />

      {/* Output box */}
      <BLChip
        x={outBox.x}
        y={outBox.y}
        w={boxW}
        h={boxH}
        label={t.stableAllocation}
        sub={t.stableAllocationSub}
        color={COLOR.sky}
      />

      {/* Arrows */}
      <BLArrow
        x1={marketBox.x + boxW}
        y1={marketBox.y + boxH / 2}
        x2={blBox.x}
        y2={blBox.y + boxH / 2 - 10}
        color={COLOR.emerald}
      />
      <BLArrow
        x1={viewBox.x + boxW}
        y1={viewBox.y + boxH / 2}
        x2={blBox.x}
        y2={blBox.y + boxH / 2 + 10}
        color={COLOR.amber}
      />
      <BLArrow x1={blBox.x + boxW} y1={blBox.y + boxH / 2} x2={outBox.x} y2={outBox.y + boxH / 2} color={COLOR.sky} />

      {/* Comparison section */}
      <text x={BL_VW / 2} y={barY - 4} textAnchor="middle" fontSize="9" fill="currentColor" fontWeight="700">
        {t.allocationComparison}
      </text>

      {/* Market equilibrium */}
      <text x={30} y={barY + 14} fontSize="8" fill={COLOR.emerald} fontWeight="600">
        {t.marketEquilibrium}
      </text>
      <MiniBar x={110} y={barY + 4} w={barW} bars={marketAlloc} />

      {/* Raw MV */}
      <text x={30} y={barY + 42} fontSize="8" fill={COLOR.rose} fontWeight="600">
        {t.meanVarianceRaw}
      </text>
      <MiniBar x={110} y={barY + 32} w={barW} bars={mvRawAlloc} />

      {/* BL output */}
      <text x={30} y={barY + 70} fontSize="8" fill={COLOR.sky} fontWeight="600">
        Black-Litterman
      </text>
      <MiniBar x={110} y={barY + 60} w={barW} bars={blAlloc} />

      {/* Annotation */}
      <line
        x1={320}
        y1={barY + 10}
        x2={320}
        y2={barY + 68}
        stroke="currentColor"
        strokeWidth="0.6"
        strokeDasharray="3 3"
        strokeOpacity={0.15}
      />
      <text x={330} y={barY + 20} fontSize="7.5" fill={COLOR.rose} fontWeight="600">
        {t.mvSkews1}
      </text>
      <text x={330} y={barY + 30} fontSize="7.5" fill={COLOR.rose} fontWeight="600">
        {t.mvSkews2}
      </text>
      <text x={330} y={barY + 52} fontSize="7.5" fill={COLOR.sky} fontWeight="600">
        {t.blBase1}
      </text>
      <text x={330} y={barY + 62} fontSize="7.5" fill={COLOR.sky} fontWeight="600">
        {t.blBase2}
      </text>
    </svg>
  );
}

export function DocBlackLittermanDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.blTitle}
      description={t.blDescription}
    >
      <BlackLittermanIllustration />
    </IllustrationCard>
  );
}

// ════════════════════════════════════════════════════════════════════
// 4. Risk Tolerance on the Frontier + allocation bars
// ════════════════════════════════════════════════════════════════════

const RT_MIN_RISK = 3.5;
const RT_MIN_RET = 1.5;
const RT_SCALE = 2.8;

function rtRet(r: number) {
  return RT_MIN_RET + RT_SCALE * Math.sqrt(r - RT_MIN_RISK);
}

// Asset colors
const ALLOC_COLOR = {
  equity: COLOR.sky, // 株式
  bond: "#f97316", // 債券 (orange)
  gold: "#eab308", // 金 (yellow)
  reit: COLOR.purple, // REIT (紫)
};

type RTAlloc = { key: string; pct: number; color: string };

type RTPersona = {
  labelIndex: number;
  r: number;
  color: string;
  alloc: RTAlloc[];
};

const RT_PERSONAS: RTPersona[] = [
  {
    labelIndex: 0,
    r: 6,
    color: COLOR.emerald,
    alloc: [
      { key: "equity", pct: 25, color: ALLOC_COLOR.equity },
      { key: "bond", pct: 60, color: ALLOC_COLOR.bond },
      { key: "gold", pct: 10, color: ALLOC_COLOR.gold },
      { key: "reit", pct: 5, color: ALLOC_COLOR.reit },
    ],
  },
  {
    labelIndex: 1,
    r: 12,
    color: COLOR.amber,
    alloc: [
      { key: "equity", pct: 35, color: ALLOC_COLOR.equity },
      { key: "bond", pct: 40, color: ALLOC_COLOR.bond },
      { key: "gold", pct: 15, color: ALLOC_COLOR.gold },
      { key: "reit", pct: 10, color: ALLOC_COLOR.reit },
    ],
  },
  {
    labelIndex: 2,
    r: 20,
    color: COLOR.rose,
    alloc: [
      { key: "equity", pct: 50, color: ALLOC_COLOR.equity },
      { key: "bond", pct: 15, color: ALLOC_COLOR.bond },
      { key: "gold", pct: 20, color: ALLOC_COLOR.gold },
      { key: "reit", pct: 15, color: ALLOC_COLOR.reit },
    ],
  },
];

function RiskToleranceIllustration() {
  const t = STRINGS[useDocLocale()];
  const VW = 520;
  const CHART_H = 240;
  const PAD = { top: 28, right: 28, bottom: 36, left: 56 };
  const CW = VW - PAD.left - PAD.right;
  const CH = CHART_H - PAD.top - PAD.bottom;
  const X_MAX = 25;
  const Y_MAX = 12;

  // Layout: dot → label (14px below dot) → dashed line → vertical bar
  const LABEL_GAP = 14; // gap from dot bottom to label top
  const LABEL_H = 12;   // label text height
  const LINE_GAP = 4;   // gap between label and dashed line start
  const VBAR_TOP = CHART_H + 20;
  const VBAR_W = 36;
  const VBAR_H = 120;
  const VH = VBAR_TOP + VBAR_H + 8;

  function toX(risk: number) {
    return PAD.left + (risk / X_MAX) * CW;
  }
  function toY(ret: number) {
    return PAD.top + ((Y_MAX - ret) / Y_MAX) * CH;
  }

  // Frontier path
  const fPts: { x: number; y: number }[] = [];
  for (let r = RT_MIN_RISK; r <= 24; r += 0.5) {
    fPts.push({ x: toX(r), y: toY(rtRet(r)) });
  }
  const frontierPath = fPts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");

  const xTicks = [0, 5, 10, 15, 20, 25];
  const yTicks = [0, 2, 4, 6, 8, 10, 12];

  const zones = [
    { x0: RT_MIN_RISK, x1: 9, color: COLOR.emerald },
    { x0: 9, x1: 16, color: COLOR.amber },
    { x0: 16, x1: 24, color: COLOR.rose },
  ];

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto w-full max-w-2xl">
      {/* Grid */}
      {yTicks.map((v) => (
        <g key={`y${v}`}>
          <line
            x1={PAD.left}
            y1={toY(v)}
            x2={VW - PAD.right}
            y2={toY(v)}
            stroke="currentColor"
            strokeWidth="0.5"
            strokeOpacity={0.08}
          />
          <text x={PAD.left - 8} y={toY(v) + 3} textAnchor="end" fontSize="8" fill="currentColor">
            {v}%
          </text>
        </g>
      ))}
      {xTicks.map((v) => (
        <g key={`x${v}`}>
          <text x={toX(v)} y={CHART_H - PAD.bottom + 14} textAnchor="middle" fontSize="8" fill="currentColor">
            {v}%
          </text>
        </g>
      ))}

      {/* Axes */}
      <line x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={CHART_H - PAD.bottom} stroke="currentColor" strokeWidth="1" />
      <line
        x1={PAD.left}
        y1={CHART_H - PAD.bottom}
        x2={VW - PAD.right}
        y2={CHART_H - PAD.bottom}
        stroke="currentColor"
        strokeWidth="1"
      />
      <text x={PAD.left} y={PAD.top - 10} fontSize="9" fontWeight="700" fill="currentColor">
        {t.expectedReturn}
      </text>
      <text x={VW - PAD.right} y={CHART_H - 6} textAnchor="end" fontSize="9" fontWeight="700" fill="currentColor">
        {t.risk}
      </text>

      {/* Zone backgrounds */}
      {zones.map((z) => (
        <rect
          key={z.x0}
          x={toX(z.x0)}
          y={PAD.top}
          width={toX(z.x1) - toX(z.x0)}
          height={CH}
          fill={z.color}
          fillOpacity={0.04}
        />
      ))}

      {/* Frontier fill + curve */}
      <path
        d={`${frontierPath} L ${fPts[fPts.length - 1].x} ${toY(0)} L ${fPts[0].x} ${toY(0)} Z`}
        fill={COLOR.sky}
        fillOpacity={0.04}
      />
      <path d={frontierPath} fill="none" stroke={COLOR.sky} strokeWidth="2.5" strokeLinecap="round" />

      {/* Direction label */}
      <text
        x={PAD.left + CW / 2}
        y={CHART_H - PAD.bottom + 30}
        textAnchor="middle"
        fontSize="9"
        fill="currentColor"
        fontWeight="600"
      >
        {t.axisHint}
      </text>

      {/* Persona dots + labels + vertical allocation bars */}
      {RT_PERSONAS.map((p) => {
        const px = toX(p.r);
        const py = toY(rtRet(p.r));
        const barX = px - VBAR_W / 2;

        // Precompute vertical segment offsets (top to bottom)
        const segOffsets = p.alloc.reduce<number[]>((acc, _a, i) => {
          acc.push(i === 0 ? VBAR_TOP : acc[i - 1] + (p.alloc[i - 1].pct / 100) * VBAR_H);
          return acc;
        }, []);

        const labelY = py + LABEL_GAP + LABEL_H;
        const lineStartY = labelY + LINE_GAP;

        return (
          <g key={p.labelIndex}>
            {/* Dot on frontier */}
            <circle cx={px} cy={py} r={7} className="fill-white dark:fill-zinc-900" stroke={p.color} strokeWidth="2.5" />
            <circle cx={px} cy={py} r={3} fill={p.color} />

            {/* Label just below dot */}
            <text x={px} y={labelY} textAnchor="middle" fontSize="9" fill={p.color} fontWeight="700">
              {t.personas[p.labelIndex]!.label}
            </text>

            {/* Dashed line: label → vertical bar */}
            <line x1={px} y1={lineStartY} x2={px} y2={VBAR_TOP - 2} stroke={p.color} strokeWidth="0.8" strokeDasharray="3 2" />

            {/* Vertical stacked bar */}
            {p.alloc.map((a, i) => {
              const segH = (a.pct / 100) * VBAR_H;
              const segY = segOffsets[i];
              return (
                <g key={a.key}>
                  <rect x={barX} y={segY} width={VBAR_W} height={segH} fill={a.color} fillOpacity={0.25} />
                  {/* Segment border */}
                  {i > 0 && (
                    <line
                      x1={barX}
                      y1={segY}
                      x2={barX + VBAR_W}
                      y2={segY}
                      stroke="currentColor"
                      strokeWidth="0.4"
                      strokeOpacity={0.1}
                    />
                  )}
                  {/* Label to the right */}
                  <text x={barX + VBAR_W + 5} y={segY + segH / 2 + 3} fontSize="7" fill={a.color} fontWeight="700">
                    {t.assets[a.key]} {a.pct}%
                  </text>
                </g>
              );
            })}
            {/* Bar border */}
            <rect
              x={barX}
              y={VBAR_TOP}
              width={VBAR_W}
              height={VBAR_H}
              rx={4}
              fill="none"
              stroke="currentColor"
              strokeWidth="0.5"
              strokeOpacity={0.12}
            />
          </g>
        );
      })}
    </svg>
  );
}

export function DocRiskToleranceFrontierDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.rtTitle}
      description={t.rtDescription}
    >
      <RiskToleranceIllustration />
    </IllustrationCard>
  );
}
